import type { App } from 'vue';
import type { ParcelContract } from '@platform/parcels/types';

/** Alias for harness context — all lifecycle methods optional for partial mocks */
export type ParcelLifecycles = Partial<ParcelContract>;

/** Lifecycle method key — derived from ParcelContract to stay in sync */
export type LifecycleKey = keyof ParcelContract;

/** Badge labels for lifecycle methods — exhaustive Record ensures compile-time sync with ParcelContract */
export const LIFECYCLE_LABELS = {
  bootstrap: 'B',
  mount: 'M',
  unmount: 'U',
  update: 'Up',
} as const satisfies Record<LifecycleKey, string>;

/** Canonical lifecycle keys — single source derived from LIFECYCLE_LABELS */
export const LIFECYCLE_KEYS = Object.keys(LIFECYCLE_LABELS) as LifecycleKey[];

export interface ParcelEntry {
  slug: string;
  name: string;
  sourcePath: string;
  load: () => Promise<ParcelLifecycles>;
}

export type ParcelStatus = 'idle' | 'loading' | 'mounted' | 'unmounted' | 'error';

export interface ParcelLifecycleState {
  status: ParcelStatus;
  errorMessage: string;
  lifecycleMethods: LifecycleKey[];
  mountCount: number;
}

export interface ParcelTimingMetrics {
  loadTimeMs: number | null;
  bootstrapTimeMs: number | null;
  mountTimeMs: number | null;
}

export type ParcelRuntimeState = ParcelLifecycleState & ParcelTimingMetrics;

export type ColorScheme = 'auto' | 'light' | 'dark';

export interface ParcelHarnessOptions {
  /** Glob result: Record<path, () => Promise<ParcelLifecycles>> (e.g. require.context) */
  parcels: Record<string, () => Promise<ParcelLifecycles>>;
  /** CSS selector or element to mount into (default: '#parcel-harness') */
  mountTo?: string | HTMLElement;
  /** Install plugins on the Vue app (e.g. i18n) */
  plugins?: (app: App) => void;
  /** Custom element tag predicate (default: tag => tag.startsWith('abt-')) */
  isCustomElement?: (tag: string) => boolean;
  /**
   * Exact parcel ID -> source file map, as `ParcelDiscoveryPlugin.entries` declares it. When given,
   * the catalog keeps these IDs instead of deriving slugs from paths, and `parcels` must be keyed by them.
   */
  entries?: Record<string, string>;
  /** Automatically open a single parcel instead of the catalog (default: true). */
  autoOpenSingleParcel?: boolean;
  /** Title shown in catalog header and console banner */
  title?: string;
  /**
   * Color scheme. 'auto' follows the host document's `data-mode` and falls back
   * to the OS preference when no host has declared one; 'light' and 'dark' pin it
   * regardless of either. Default: 'auto'
   */
  colorScheme?: ColorScheme;
}

export interface MockShellApiOptions {
  apiBaseUrl?: string;
  accessToken?: string;
  isoLocale?: string;
  locale?: string;
}
